import { userFetchGiftCoupon } from '@/views/restaurant-information/api'
import { CustomSuccessData } from 'axios'
import CouponInformation from '@/utils/share-coupon-manager/models/CouponInformation'
import API from '@/network/API'
import ShopInformation from '@/utils/shop-information-manager/models/ShopInformation'

interface GroupCouponResult {
  coupon: CouponInformation
  restaurant: {
    restaurantId: string
    name: string
    logo: string
    street: string
    city: string
    state: string
  }
}

/**
 * 用户优惠券数据结构(对应API文档中的UserCouponsVo)
 * 这是专门用于优惠券领取页面的接口返回类型
 */
interface UserCouponsVo {
  amount: number
  conditionAmount: number
  conditionCloseHour: number
  conditionCloseMinute: number
  conditionEndDate: string
  conditionExpiryDays: number
  conditionOpenHour: number
  conditionOpenMinute: number
  conditionPoints: number
  conditionStartDate: string
  conditionWeekdays: { day: number }[]
  count: number
  couponName: string
  discountDescription: string
  giftCouponId: string
  isAccept: boolean
  isGiveEnabled: boolean
  isOnlyShowEndDate: boolean
  isUnionUse: boolean
  itemIds: string[]
  logo: string
  name: string
  receiveCount: number
  restaurantInfo: ShopInformation
  type: number
  useType: number
  userGiftCouponId: string
}

const _baseUrl = 'restaurants/v1.0'

/**
 * 获取用户在该餐厅的所有优惠券(专用于优惠券领取页面)
 * API文档: /restaurants/v1.0/{restaurantId}/user/giftCoupons
 * @param restaurantId 餐厅ID
 * @returns 用户优惠券列表
 */
export async function getUserGiftCoupons(
  restaurantId: string
): Promise<CustomSuccessData<UserCouponsVo[]>> {
  return API.instance().get<UserCouponsVo[]>(
    `${_baseUrl}/${restaurantId}/user/giftCoupons`
  )
}

/**
 * 获取团餐优惠券信息(优惠券领取页面专用)
 * @param restaurantId 餐厅ID（来自URL参数rid）
 * @param couponId 优惠券ID（来自URL参数cid）
 * @returns 优惠券详细信息和餐厅信息
 *
 * 实现逻辑：
 * 1. 调用专用接口 getUserGiftCoupons 获取用户在该餐厅的所有优惠券
 * 2. 从返回数据中根据 couponId 查找目标优惠券
 * 3. 根据优惠券的 count 判断库存状态 (count===-1表示不限量)
 * 4. 从优惠券的 restaurantInfo 字段获取餐厅信息
 */
export async function getGroupCouponInfo(
  restaurantId: string,
  couponId: string
): Promise<CustomSuccessData<GroupCouponResult>> {
  // 调用专用接口获取用户在该餐厅的所有优惠券
  const result = await getUserGiftCoupons(restaurantId)

  // 从优惠券列表中查找目标优惠券
  const giftCoupons = result.data || []
  const targetCoupon = giftCoupons.find(
    (coupon: UserCouponsVo) => coupon.giftCouponId === couponId
  )

  if (!targetCoupon) {
    // 优惠券不存在
    const error = new Error('优惠券不存在或已下架')
    Object.assign(error, { code: 50055 })
    return Promise.reject(error)
  }

  // 检查库存状态
  // count === -1 表示不限制数量
  // count === 0 表示已领取完
  const { count } = targetCoupon

  if (count !== -1 && count <= 0) {
    // 已领取完
    const error = new Error('您来晚了，优惠券已被领完')
    Object.assign(error, { code: 50052 })
    return Promise.reject(error)
  }

  // 从优惠券数据中提取餐厅信息
  const restaurantInfo = targetCoupon.restaurantInfo
  if (!restaurantInfo) {
    // 餐厅信息缺失
    const error = new Error('餐厅信息缺失')
    Object.assign(error, { code: 50056 })
    return Promise.reject(error)
  }

  // 返回优惠券信息和餐厅信息
  // 将UserCouponsVo转换为CouponInformation格式
  const couponInfo: CouponInformation = {
    amount: targetCoupon.amount,
    conditionAmount: targetCoupon.conditionAmount,
    conditionCloseHour: targetCoupon.conditionCloseHour,
    conditionCloseMinute: targetCoupon.conditionCloseMinute,
    conditionEndDate: targetCoupon.conditionEndDate,
    conditionExpiryDays: targetCoupon.conditionExpiryDays,
    conditionOpenHour: targetCoupon.conditionOpenHour,
    conditionOpenMinute: targetCoupon.conditionOpenMinute,
    conditionPoints: targetCoupon.conditionPoints,
    conditionStartDate: targetCoupon.conditionStartDate,
    conditionWeekdays: targetCoupon.conditionWeekdays,
    count: targetCoupon.count,
    couponName: targetCoupon.couponName,
    giftCouponId: targetCoupon.giftCouponId,
    isAccept: targetCoupon.isAccept,
    isGiveEnabled: targetCoupon.isGiveEnabled,
    isOnlyShowEndDate: targetCoupon.isOnlyShowEndDate,
    isUnionUse: targetCoupon.isUnionUse,
    logo: targetCoupon.logo,
    name: targetCoupon.name,
    type: targetCoupon.type,
    useType: targetCoupon.useType,
    userGiftCouponId: targetCoupon.userGiftCouponId,
    restaurantInfo: restaurantInfo,
    itemIds: targetCoupon.itemIds,
    discountDescription: targetCoupon.discountDescription
  }

  return {
    ...result,
    data: {
      coupon: couponInfo,
      restaurant: {
        restaurantId: restaurantInfo.restaurantId,
        name: restaurantInfo.name,
        logo: restaurantInfo.logo,
        street: restaurantInfo.street,
        city: restaurantInfo.city,
        state: restaurantInfo.state
      }
    }
  }
}

/**
 * 领取团餐优惠券
 * 复用现有的 userFetchGiftCoupon API
 * @param restaurantId 餐厅ID
 * @param couponId 优惠券ID
 */
export async function claimGroupCoupon(
  restaurantId: string,
  couponId: string
): Promise<CustomSuccessData<void>> {
  return userFetchGiftCoupon(restaurantId, couponId)
}
